import { useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { WorkspaceType } from '../../graphql/enums';

export type ImpersonationDraft = {
  workspaceId?: string;
  workspaceName?: string;
  workspaceType?: WorkspaceType;
  userId?: string;
  userName?: string;
  subscriptionTiers: string[];
  types: string[];
};

const IMPERSONATION_DRAFT_KEY = ['impersonationDraft'] as const;

const emptyDraft: ImpersonationDraft = {
  subscriptionTiers: [],
  types: [],
};

export const useImpersonationDraft = () => {
  const queryClient = useQueryClient();

  // Pure client-state held in the query cache: seeded with initialData and
  // mutated only via setDraft (setQueryData). The query must never actually
  // fetch — an enabled queryFn resolves to emptyDraft on mount and races with
  // setDraft, clobbering a just-written draft back to empty.
  const { data } = useQuery<ImpersonationDraft>({
    queryKey: IMPERSONATION_DRAFT_KEY,
    queryFn: () =>
      queryClient.getQueryData<ImpersonationDraft>(IMPERSONATION_DRAFT_KEY) ??
      emptyDraft,
    enabled: false,
    initialData: emptyDraft,
    staleTime: Infinity,
    gcTime: Infinity,
  });

  const draft = data ?? emptyDraft;

  const setDraft = useCallback(
    (patch: Partial<ImpersonationDraft>) => {
      queryClient.setQueryData<ImpersonationDraft>(
        IMPERSONATION_DRAFT_KEY,
        (prev) => ({ ...(prev ?? emptyDraft), ...patch })
      );
    },
    [queryClient]
  );

  const clearDraft = useCallback(() => {
    queryClient.removeQueries({ queryKey: IMPERSONATION_DRAFT_KEY });
  }, [queryClient]);

  return { draft, setDraft, clearDraft };
};
